import { Box, CircularProgress, Container, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { useQueryClient } from "@tanstack/react-query"

import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { UserPaymentAccountQuery } from "../../../../home/components/wallets/walletsApi"
import { usePaymentFlow } from "../hooks/paymentHooks"
import { useDateTime } from "../../../../../../localization/useDateTime"
import { useNavigate, useParams } from "react-router-dom"
import { useEffectOnce } from "../../../../../../../shared/hooks/useEffectOnce"
import { useEffect } from "react"
import { PaymentErrorType, PaymentStatus } from "../types/paymentTypes"
import {
    USER_REPORTS_SETTINGS_WALLETS_STATUS_FAILED_ROUTE,
    USER_REPORTS_SETTINGS_WALLETS_STATUS_SUCCESS_ROUTE,
} from "../../../../../../../constants/routes"
import { PageHeader } from "../../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../../shared/components/pageHeader/PageHeaderApi"

const useStyles = makeStyles()((theme) => ({
    container: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: theme.spacing(3),
    },
    content: {
        padding: theme.spacing(6),
        maxWidth: 400,
        width: "100%",
        textAlign: "center",
    },
    title: {
        fontWeight: 600,
        marginBottom: theme.spacing(2),
        color: theme.palette.text.primary,
    },
    description: {
        color: theme.palette.text.secondary,
        lineHeight: 1.6,
    },
    progressContainer: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: theme.spacing(2),
    },
    loaderContainer: {
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: theme.spacing(2),
    },
    countDown: {
        position: "absolute",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
    },
}))

export function UserWalletsRefillStatusPoll() {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()
    const { orderUid } = useParams()
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    const { progressState, paymentState, pollForPaymentState } = usePaymentFlow()

    useEffectOnce(() => {
        if (orderUid) {
            pollForPaymentState(orderUid)
        }
    })

    useEffect(() => {
        if (paymentState.status === PaymentStatus.FAILED) {
            if (paymentState.errorType === PaymentErrorType.TIMEOUT) {
                navigate(`${USER_REPORTS_SETTINGS_WALLETS_STATUS_FAILED_ROUTE}?type=timeout`)
            } else {
                navigate(`${USER_REPORTS_SETTINGS_WALLETS_STATUS_FAILED_ROUTE}?type=failed`)
            }
        }

        if (paymentState.status === PaymentStatus.COMPLETED) {
            queryClient.invalidateQueries({ queryKey: [UserPaymentAccountQuery.USER_PAYMENT_ACCOUNT] })
            navigate(USER_REPORTS_SETTINGS_WALLETS_STATUS_SUCCESS_ROUTE, {
                state: {
                    response: paymentState.response.transactionResponse,
                },
            })
        }
    }, [paymentState])

    return (
        <>
            <LocalizedStrict id="user-wallets-refill-title" attrs={{ title: true }}>
                <PageHeader page={Page.USER_WALLETS_REFILL} title="Refill wallet" />
            </LocalizedStrict>
            <Container className={classes.container}>
                <Box className={classes.content}>
                    <LocalizedStrict id="user-wallets-refill-payment-wait-label">
                        <Typography variant="h5" className={classes.title}>
                            Processing Payment
                        </Typography>
                    </LocalizedStrict>

                    {progressState?.remaining && (
                        <Box className={classes.loaderContainer}>
                            <CircularProgress size={100} thickness={4} />
                            <Box className={classes.countDown}>
                                <span className="text-lg font-bold text-gray-600">
                                    {dateTimeFactory.formatDuration(progressState.remaining)}
                                </span>
                            </Box>
                        </Box>
                    )}

                    <LocalizedStrict id="user-wallets-refill-payment-wait-description">
                        <Typography variant="body1" className={classes.description}>
                            Please wait while we process your payment. This may take a few moments.
                        </Typography>
                    </LocalizedStrict>
                </Box>
            </Container>
        </>
    )
}
